<script setup lang="ts">
/**
 * Bridge component: wraps XInput with Vue 2 v-model compat and vee-validate defaults.
 *
 * Purpose:
 * - Adapts Vue 2 v-model (value/input) to Vue 3 XInput (modelValue/update:modelValue)
 * - Enables vee-validate integration on all input fields for Zod-based validation
 * - Required while using @vue/compat in MODE: 2
 *
 * Once compat is removed or MODE is set to 3 globally, evaluate if direct XInput mapping
 * in global-components.ts is sufficient or if vee-validate defaults should be preserved.
 */
import { XInput } from '@swordhealth/ui'

const props = defineProps<{
  value?: string | number
  type?: string
  validation?: { $touch: () => void }
}>()

const emit = defineEmits<{
  (e: 'input', value: string | number | boolean | object): void
}>()

function handleUpdate(newValue: string | number | boolean | object) {
  props.validation?.$touch()
  emit('input', newValue)
}
</script>

<template>
  <x-input
    v-bind="$attrs"
    :type="type"
    :model-value="value"
    :validation-triggers="{ onBlur: true, onChange: true, onInput: true }"
    @update:model-value="handleUpdate"
  />
</template>
